import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { RichTextEditor } from "../components/RichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  FileText,
  Pencil,
  Trash2,
  Mail,
  Lock,
  Copy,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "intro", label: "Introduction" },
  { value: "follow_up", label: "Follow Up" },
  { value: "proposal", label: "Proposal" },
  { value: "thank_you", label: "Thank You" },
  { value: "general", label: "General" },
];

const categoryColors = {
  intro: "bg-blue-50 text-blue-700",
  follow_up: "bg-amber-50 text-amber-700",
  proposal: "bg-emerald-50 text-emerald-700",
  thank_you: "bg-violet-50 text-violet-700",
  general: "bg-slate-100 text-slate-600"
};

const emptyForm = { name: "", subject: "", body: "", category: "general" };

export default function EmailTemplatesPage() {
  const [defaults, setDefaults] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/email-templates`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDefaults(data.templates || []);
        setCustom(data.custom || []);
      }
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl.template_id);
    setForm({ name: tpl.name, subject: tpl.subject, body: tpl.body, category: tpl.category || "general" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("Name, subject, and body are required");
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API}/email-templates/${editing}`
        : `${API}/email-templates`;
      const method = editing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editing ? "Template updated" : "Template created");
        setDialogOpen(false);
        setForm({ ...emptyForm });
        setEditing(null);
        fetchTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save template");
      }
    } catch (e) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      const res = await fetch(`${API}/email-templates/${templateId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Template deleted");
        fetchTemplates();
      } else {
        toast.error("Failed to delete template");
      }
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicate = (tpl) => {
    setEditing(null);
    setForm({
      name: `${tpl.name} (Copy)`,
      subject: tpl.subject,
      body: tpl.body,
      category: tpl.category || "general"
    });
    setDialogOpen(true);
  };

  const TemplateCard = ({ tpl, isDefault }) => (
    <Card
      className={`hover:shadow-md transition-shadow ${preview?.name === tpl.name && !isDefault ? "ring-2 ring-primary/30" : ""}`}
      data-testid={`template-card-${tpl.template_id || tpl.name}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FileText className={`w-4 h-4 ${isDefault ? "text-slate-400" : "text-primary"}`} />
              <h3 className="font-medium text-slate-900 truncate">{tpl.name}</h3>
              {isDefault && (
                <Badge variant="outline" className="text-xs flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" />
                  Default
                </Badge>
              )}
            </div>
            <Badge className={`${categoryColors[tpl.category] || categoryColors.general} text-xs mb-2`}>
              {CATEGORIES.find(c => c.value === tpl.category)?.label || tpl.category}
            </Badge>
            <p className="text-sm text-slate-600 font-medium mt-1">{tpl.subject}</p>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
              {tpl.body.replace(/<[^>]*>/g, "").substring(0, 120)}...
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPreview(preview?.name === tpl.name ? null : tpl)}
              data-testid={`preview-${tpl.template_id || tpl.name}`}
            >
              <Mail className="w-4 h-4 text-slate-400" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleDuplicate(tpl)}
              data-testid={`duplicate-${tpl.template_id || tpl.name}`}
            >
              <Copy className="w-4 h-4 text-slate-400" />
            </Button>
            {!isDefault && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => openEdit(tpl)}
                  data-testid={`edit-${tpl.template_id}`}
                >
                  <Pencil className="w-4 h-4 text-slate-400" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleDelete(tpl.template_id)}
                  data-testid={`delete-${tpl.template_id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Preview expansion */}
        {preview?.name === tpl.name && (
          <div className="mt-3 pt-3 border-t bg-slate-50 -mx-4 -mb-4 p-4 rounded-b-lg">
            <p className="text-xs text-slate-500 mb-1 font-medium">Subject:</p>
            <p className="text-sm text-slate-700 mb-3">{tpl.subject}</p>
            <p className="text-xs text-slate-500 mb-1 font-medium">Body:</p>
            <div className="text-sm text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: tpl.body }} />
            <p className="text-xs text-slate-400 mt-3">
              Variables: {"{{contact_name}}, {{business_name}}, {{sender_name}}, {{company_name}}"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="email-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Email Templates
          </h1>
          <p className="text-slate-500 mt-1">
            Manage reusable email templates for outreach
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Custom Templates */}
      {custom.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">
            Custom Templates ({custom.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {custom.map((tpl) => (
              <TemplateCard key={tpl.template_id} tpl={tpl} isDefault={false} />
            ))}
          </div>
        </div>
      )}

      {/* Default Templates */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">
          Default Templates ({defaults.length})
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {defaults.map((tpl, idx) => (
            <TemplateCard key={idx} tpl={tpl} isDefault={true} />
          ))}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription>
              Use variables like {"{{contact_name}}, {{business_name}}, {{sender_name}}, {{company_name}}"} in subject and body.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Special Offer Follow-up"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="template-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="e.g., Quick follow-up from {{company_name}}"
                data-testid="template-subject-input"
              />
            </div>
            <div>
              <Label>Body *</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={form.body}
                  onChange={(html) => setForm({ ...form, body: html })}
                  placeholder="Write your email template... Use variables like {{contact_name}}, {{business_name}}"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
